import { useEffect, useState } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { RoutePath } from 'src/enums/routePath';
import { useSetUserInfo } from 'src/redux/account/accountSlice';
import { getProfile } from 'src/services/profile';
import { Button, Flex, Layout, MenuProps } from 'core-ui';
import Header from './components/Header';
import Logo from './components/Logo';
import {
  ACCESS_TOKEN_KEY,
  COMPANY_ID,
  HAS_COMPANY_LICENCE_RENEWAL_ORDER,
  IS_EXPIRED_RENEWAL,
  IS_REDIRECT_TO_CHANGE_PASSWORD,
  LICENCE_EXPIRY_AT,
  PROFILE_NAME,
  ROLE,
  ROLE_COMPANY_ADMIN,
  ROLE_COMPANY_STAFF,
  ROLE_COMPANY_SUPER_ADMIN,
  ROLE_SYSTEM_ADMIN,
  ROLE_SYSTEM_SUPER_ADMIN
} from 'src/constants/auth';
import { checkIsSystemSuperAdminOrSystemAdmin } from 'src/utils/common';
import { permissionRouteMap } from 'src/pages/Login';

const MainLayout = () => {
  const dispatchUserInfo = useSetUserInfo();
  const navigate = useNavigate();
  const [role, setRole] = useState(null);
  const [isProductManagement, setIsProductManagement] = useState(false);
  const [permissionsSystemAdmin, setPermissionSystemAdmins] = useState<
    string[]
  >([]);

  const isShowCompanyRegistrations =
    role === ROLE_SYSTEM_SUPER_ADMIN ||
    (role === ROLE_SYSTEM_ADMIN &&
      permissionsSystemAdmin?.includes('company_registration'));
  const isShowCompanyAdmin =
    role === ROLE_SYSTEM_SUPER_ADMIN ||
    (role === ROLE_SYSTEM_ADMIN &&
      permissionsSystemAdmin?.includes('company_admin')) ||
    (role === ROLE_COMPANY_SUPER_ADMIN && isProductManagement);
  const isShowCompanyStaff =
    role === ROLE_SYSTEM_SUPER_ADMIN ||
    (role === ROLE_SYSTEM_ADMIN &&
      permissionsSystemAdmin?.includes('company_staff')) ||
    role === ROLE_COMPANY_ADMIN ||
    (role === ROLE_COMPANY_SUPER_ADMIN && isProductManagement);
  const isShowHistoryScan =
    role === ROLE_SYSTEM_SUPER_ADMIN ||
    (role === ROLE_SYSTEM_ADMIN &&
      permissionsSystemAdmin?.includes('scanning_histories')) ||
    role === ROLE_COMPANY_ADMIN ||
    (role === ROLE_COMPANY_SUPER_ADMIN && isProductManagement);
  const isShowProduct =
    role === ROLE_SYSTEM_SUPER_ADMIN ||
    (role === ROLE_SYSTEM_ADMIN &&
      permissionsSystemAdmin?.includes('company_product')) ||
    role === ROLE_COMPANY_ADMIN ||
    role === ROLE_COMPANY_STAFF ||
    (role === ROLE_COMPANY_SUPER_ADMIN && isProductManagement);
  const isShowReport =
    role === ROLE_SYSTEM_SUPER_ADMIN ||
    (role === ROLE_SYSTEM_ADMIN && permissionsSystemAdmin?.includes('reports'));
  const isShowSticker =
    role === ROLE_SYSTEM_SUPER_ADMIN ||
    (role === ROLE_SYSTEM_ADMIN &&
      permissionsSystemAdmin?.includes('product_sticker_order'));
  const isShowRenewal =
    role === ROLE_SYSTEM_SUPER_ADMIN ||
    (role === ROLE_SYSTEM_ADMIN &&
      permissionsSystemAdmin?.includes('licence_renewal_order'));
  const isShowAnalytic =
    role === ROLE_SYSTEM_SUPER_ADMIN ||
    (role === ROLE_SYSTEM_ADMIN &&
      permissionsSystemAdmin?.includes('analysis'));
  const isShowSystemAdmin = role === ROLE_SYSTEM_SUPER_ADMIN;
  const isShowSetting = role === ROLE_SYSTEM_SUPER_ADMIN;

  const menuItems: MenuProps['items'] = [
    {
      key: RoutePath.COMPANY_REGISTRATION_MANAGEMENT,
      icon: <img src="/svg/note.svg" />,
      label: (
        <Link to={RoutePath.COMPANY_REGISTRATION_MANAGEMENT}>
          Company Registrations
        </Link>
      ),
      className: `!w-full after:!border-e-0 ${isShowCompanyRegistrations ? '' : '!hidden'}`
    },
    {
      key: RoutePath.COMPANY,
      icon: <img src="/svg/company.svg" />,
      label: (
        <Link
          to={
            checkIsSystemSuperAdminOrSystemAdmin(Number(role))
              ? RoutePath.COMPANY
              : `${RoutePath.COMPANY}${RoutePath.DETAIL}/${localStorage.getItem(COMPANY_ID)}`
          }
        >
          {checkIsSystemSuperAdminOrSystemAdmin(Number(role))
            ? 'Companies'
            : 'Company Information'}
        </Link>
      ),
      className: `!w-full after:!border-e-0 ${Number(role) === ROLE_SYSTEM_ADMIN && !permissionsSystemAdmin?.includes('company') ? '!hidden' : ''}`
    },
    {
      key: RoutePath.COMPANY_ADMIN,
      icon: <img src="/svg/company-admin.svg" />,
      label: <Link to={RoutePath.COMPANY_ADMIN}>Company Admin</Link>,
      className: `!w-full after:!border-e-0 ${isShowCompanyAdmin ? '' : '!hidden'}`
    },
    {
      key: RoutePath.COMPANY_STAFF,
      icon: <img src="/svg/company-staff.svg" />,
      label: <Link to={RoutePath.COMPANY_STAFF}>Company Staff</Link>,
      className: `!w-full after:!border-e-0 ${isShowCompanyStaff ? '' : '!hidden'}`
    },
    {
      key: RoutePath.SYSTEM_ADMIN,
      icon: <img src="/svg/user.svg" />,
      label: <Link to={RoutePath.SYSTEM_ADMIN}>System admin</Link>,
      className: `!w-full after:!border-e-0 ${isShowSystemAdmin ? '' : '!hidden'}`
    },
    {
      key: RoutePath.PRODUCT,
      icon: <img src="/svg/product.svg" />,
      label: <Link to={RoutePath.PRODUCT}>Company Product</Link>,
      className: `!w-full after:!border-e-0 ${isShowProduct ? '' : '!hidden'}`
    },
    {
      key: RoutePath.STICKER,
      icon: <img src="/svg/sticker.svg" />,
      label: <Link to={RoutePath.STICKER}>Sticker purchase list</Link>,
      className: `!w-full after:!border-e-0 ${isShowSticker ? '' : '!hidden'}`
    },
    {
      key: RoutePath.HISTORY_SCAN,
      icon: <img src="/svg/scan-barcode.svg" />,
      label: <Link to={RoutePath.HISTORY_SCAN}>Scanning Histories</Link>,
      className: `!w-full after:!border-e-0 ${isShowHistoryScan ? '' : '!hidden'}`
    },
    {
      key: RoutePath.RENEWAL,
      icon: <img src="/svg/renewal.svg" />,
      label: <Link to={RoutePath.RENEWAL}>Renewal management</Link>,
      className: `!w-full after:!border-e-0 ${isShowRenewal ? '' : '!hidden'}`
    },
    {
      key: RoutePath.REPORT,
      icon: <img src="/svg/report.svg" />,
      label: <Link to={RoutePath.REPORT}>Report</Link>,
      className: `!w-full after:!border-e-0 ${isShowReport ? '' : '!hidden'}`
    },
    {
      key: RoutePath.ANALYTIC,
      icon: <img src="/svg/analytic.svg" />,
      label: <Link to={RoutePath.ANALYTIC}>Analytic</Link>,
      className: `!w-full after:!border-e-0 ${isShowAnalytic ? '' : '!hidden'}`
    },
    {
      key: RoutePath.SETTING,
      icon: <img src="/svg/setting.png" />,
      label: <Link to={RoutePath.SETTING}>Setting</Link>,
      className: `!w-full after:!border-e-0 ${isShowSetting ? '' : '!hidden'}`
    }
  ];

  const getSelectedKey = () => {
    const path = location.pathname;
    for (let item of menuItems) {
      if (!item?.key) return '';
      // First check if the path exactly matches the menu item key
      if (path === item.key) {
        return item.key; // Exact match
      }
      // If not an exact match, check if path starts with key and is followed by a slash or the end of the string
      if (
        path.startsWith(`${item.key}`) &&
        (path.length === `${item.key}`.length ||
          path[`${item.key}`.length] === '/')
      ) {
        return item.key; // Match sub-paths like `/company/create`, `/company/edit`
      }
    }
    return '';
  };

  const handleLogout = () => {
    localStorage.setItem(IS_REDIRECT_TO_CHANGE_PASSWORD, 'false');
    localStorage.setItem(IS_EXPIRED_RENEWAL, 'false');
    Cookies.remove(ACCESS_TOKEN_KEY);
    window.location.replace(RoutePath.LOGIN);
  };

  const handleRedirectPageForSystemAdmin = (permissions: string[]) => {
    const order = Object.keys(permissionRouteMap);
    const sortedPermissions = [...permissions]?.sort(
      (a, b) => order.indexOf(a) - order.indexOf(b)
    );
    const matchedRoute = sortedPermissions?.find(
      (permission) => permissionRouteMap[permission]
    );

    if (matchedRoute) {
      navigate(permissionRouteMap[matchedRoute]);
    } else {
      navigate(RoutePath.NOT_PERMISSION);
    }
  };

  const handleGetProfile = async () => {
    try {
      const res = await getProfile({ with: 'company', with_count: '' });
      if (res?.data?.role) {
        dispatchUserInfo.setUserInfo(res?.data);
        localStorage.setItem(ROLE, res?.data?.role);
        localStorage.setItem(PROFILE_NAME, res?.data?.name);
        localStorage.setItem(COMPANY_ID, res?.data?.company_id);
        setRole(res?.data?.role);
        setIsProductManagement(res?.data?.company?.is_product_management);
        setPermissionSystemAdmins(
          res?.data?.permissions?.map((item: any) => item?.permission_key)
        );
        localStorage.setItem(
          LICENCE_EXPIRY_AT,
          res?.data?.company?.licence_expiry_at
        );
        localStorage.setItem(
          HAS_COMPANY_LICENCE_RENEWAL_ORDER,
          res?.data?.company?.has_company_licence_renewal_order
        );
        const today = new Date();
        const licenceExpiryAt = new Date(res?.data?.company?.licence_expiry_at);
        const isCompanySuperAdmin =
          res?.data?.role === ROLE_COMPANY_SUPER_ADMIN;
        const isExpired =
          res?.data?.company?.licence_expiry_at &&
          today?.valueOf() > licenceExpiryAt.valueOf() &&
          isCompanySuperAdmin;
        const isRequiredRenewal =
          (isCompanySuperAdmin &&
            res?.data?.company?.has_company_licence_renewal_order !== null) ||
          isExpired;
        // prevent click to another page => redirect to company detail and show button renewal or popup waiting
        localStorage.setItem(IS_EXPIRED_RENEWAL, `${isRequiredRenewal}`);
        if (isRequiredRenewal) {
          `${RoutePath.COMPANY}${RoutePath.DETAIL}/${res?.data?.company_id}`;
        }
        const permissionKeys = res?.data?.permissions?.map(
          (item: any) => item?.permission_key
        );
        if (Number(res?.data?.role) === ROLE_SYSTEM_ADMIN) {
          handleRedirectPageForSystemAdmin(permissionKeys);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    handleGetProfile();
    const intervalId = setInterval(() => {
      handleGetProfile();
    }, 300000);
    return () => clearInterval(intervalId);
  }, []);
  return (
    <Layout
      headerContent={<Header />}
      logoContent={<Logo />}
      logoClassName="flex justify-center items-center !bg-[#006B3F] ![height:unset] px-[24px] py-[12px] text-center"
      menuItems={menuItems}
      menuProps={{ selectedKeys: [getSelectedKey().toString()] }}
      siderStyle={{
        overflowX: 'hidden'
      }}
      contentStyle={{
        overflow: 'auto',
        backgroundColor: 'unset'
      }}
      siderFooterContent={
        <Button
          onClick={handleLogout}
          className="p-[8px] w-[181px] h-[40px] cursor-pointer rounded-[7px]"
        >
          <Flex className="gap-x-[10px]">
            <img src="/svg/logout.svg" alt="icon-logout" />
            <span className="text-[14px] text-[#FF5045]">Logout</span>
          </Flex>
        </Button>
      }
      siderFooterClassName="!bg-[#006B3F] !border-t-[#006B3F] flex justify-center !pb-[40px]"
    >
      <Outlet />
    </Layout>
  );
};

export default MainLayout;
